import {Component, Input, OnInit} from '@angular/core';
import {filter, first, switchMap, takeUntil} from "rxjs/operators";
import {Website} from "../../../website";
import * as fromStore from "../../../../../store";
import {combineLatest, Subject} from "rxjs";
import {Store} from "@ngrx/store";
import {EcommerceService} from "../../ecommerce.service";
import {TaxSettings} from "./mapper/tax-settings";
import {DbResponse} from "../../../../../shared/interceptors/db-response";
import {NotifierComponent} from "../../../../../shared/components/notifier/notifier.component";
import {Notification} from "../../../../../shared/components/notifier/notification";
import {NotificationType} from "../../../../../shared/components/notifier/notification-type";
import {FormControl, FormGroup} from "@angular/forms";
import {Product} from "../../../product";
import {GoogleAnalyticsService} from "../../../../../shared/services/google-analytics/google-analytics.service";

@Component({
    selector: 'db-ecommerce-tax-settings',
    templateUrl: './tax-settings.component.html',
    styleUrls: ['./tax-settings.component.scss'],
    standalone: false
})
export class EcommerceTaxSettingsComponent implements OnInit {
  @Input() website: Website;
  @Input() taxPlugin: Product;
  public basedOnSettings: TaxSettings;
  public shippingClassSettings: TaxSettings;
  public taxTotalSettings: TaxSettings;
  public taxSettingsForm: FormGroup;
  public connectTaxesEnabled: boolean;
  public loading: boolean;
  public loadingBtn: boolean;
  private readonly _destroyStream$ = new Subject<boolean>();

  constructor(
    private readonly _store: Store<fromStore.DbStoreState>,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _ecommerceService: EcommerceService
  ) { }

  ngOnInit() {
    this._getTaxSettings();
    this._getGeneralSettings();
  }

  private _getTaxSettings(): void {
    this._store.select(fromStore.getEcommerceTaxSettingsLoaded(this.website?.id))
        .pipe(
            filter((loaded: boolean) => loaded),
            switchMap(() => combineLatest(
                this._store.select(fromStore.getEcommerceTaxBasedOn(this.website?.id)),
                this._store.select(fromStore.getEcommerceTaxShippingClass(this.website?.id)),
                this._store.select(fromStore.getEcommerceTaxTotalDisplay(this.website?.id))
            )),
          filter(([basedOn, shippingClass, totalDisplay]) => !!basedOn?.id && !!shippingClass?.id && !!totalDisplay?.id),
          takeUntil(this._destroyStream$)
        )
        .subscribe(([basedOn, shippingClass, totalDisplay]) => {
          this.basedOnSettings = basedOn;
          this.shippingClassSettings = shippingClass;
          this.taxTotalSettings = totalDisplay;
          this.taxSettingsForm = new FormGroup({
            basedOn: new FormControl(basedOn?.value),
            shippingClass: new FormControl(shippingClass?.value || 'standard'),
            taxTotal: new FormControl(totalDisplay?.value)
          });
        });
  }

  private _getGeneralSettings(): void {
    this._store.select(fromStore.getEcommerceGeneralSettingsLoaded(this.website?.id))
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getEcommerceConnectTaxesEnabled(this.website?.id))
        )),
        filter(([connectTaxesEnabled]) => !!connectTaxesEnabled ),
        takeUntil(this._destroyStream$)
      )
      .subscribe(([connectTaxesEnabled]) => {
        this.connectTaxesEnabled = connectTaxesEnabled?.value === 'yes';
      });
  }

  private _sendEvent(label): void {
    const analyticsData = {
      eventCategory: 'Dashboard Action',
      eventAction: 'Ecommerce - Tax',
      eventLabel: label
    };
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
  }

  public saveTaxSettings(): void {
    this.loadingBtn = true;
    this._ecommerceService.updateTaxSettings(this.website?.id, this.taxSettingsForm.value)
      .pipe(filter((response: DbResponse): boolean => !!response),
        first())
      .subscribe((response: DbResponse): void => {
        this.loadingBtn = false;
        if (response.success && response?.data) {
          NotifierComponent.notify(new Notification(response.message, NotificationType.SUCCESS));
          this._store.dispatch(new fromStore.LoadEcommerceTaxSettings({websiteId: this.website?.id}));
          this._store.dispatch(new fromStore.LoadEcommerceGeneralSettings({websiteId: this.website?.id}));
        }
        else {
          NotifierComponent.notify(new Notification(response.message, NotificationType.DANGER));
        }
      });
  }

  public disableCalcTaxes(): void {
    this.loading = true;
    this._ecommerceService.enableCalcTaxes(this.website?.id, false)
        .pipe(filter((response: DbResponse): boolean => !!response),
            first())
        .subscribe((response: DbResponse): void => {
          if (response.success && response?.data) {
            this._store.dispatch(new fromStore.LoadEcommerceGeneralSettings({websiteId: this.website?.id}));
            this._store.dispatch(new fromStore.LoadEcommerceTaxSettings({websiteId: this.website?.id}));
          }
          else {
            this.loading = false;
            NotifierComponent.notify(new Notification(response.message, NotificationType.DANGER));
          }
        });
  }

}
